// Per-field image upload contracts for admin photo surfaces. Validation runs
// BEFORE any crop UI opens. Dimensions / low-res checks are separate so a
// soft warning never blocks an otherwise-valid pick.

const BYTES_PER_MB = 1024 * 1024;

export const ImageMime = {
  PNG: 'image/png',
  JPEG: 'image/jpeg',
  GIF: 'image/gif',
  WEBP: 'image/webp',
  SVG: 'image/svg+xml',
} as const;

export type ImageMimeType = (typeof ImageMime)[keyof typeof ImageMime];

export const ImageFieldKind = {
  PHOTO: 'photo',
  COVER: 'cover',
  BANNER: 'banner',
  LOGO: 'logo',
} as const;

export type ImageFieldKind = (typeof ImageFieldKind)[keyof typeof ImageFieldKind];

export interface PixelSize {
  width: number;
  height: number;
}

/**
 * Largest stored size for an uncropped upload. The image is scaled down to fit
 * inside it, never up. `maxHeight: null` bounds the width only.
 */
export interface ImageBounds {
  maxWidth: number;
  maxHeight: number | null;
}

/** One admin image field's accept / size / crop / warn contract. */
export interface ImageFieldSpec {
  kind: ImageFieldKind;
  /** Human label used in helper and error copy ("Photo", "Logo", …). */
  label: string;
  acceptedTypes: readonly string[];
  /** Value for `<input accept="…">`. */
  acceptAttr: string;
  /** Spoken type list for error copy: "PNG, JPG, GIF or WebP". */
  typesLabel: string;
  maxMb: number;
  maxBytes: number;
  /**
   * Locked crop aspect (width / height). `null` means no crop step — the file
   * uploads as-is (Logo, and any future vector-only path).
   */
  aspectRatio: number | null;
  /** Short aspect label for helpers ("1:1", "16:9"). Null when uncropped. */
  cropLabel: string | null;
  /**
   * Fixed stored pixel size written by the crop export. Null when uncropped
   * (Logo has no fixed stored size).
   */
  output: PixelSize | null;
  /**
   * Soft low-res threshold. Raster sources below either edge get a warning;
   * SVG never triggers it.
   */
  warnBelow: PixelSize;
  /** Ideal source size quoted in helper + warning copy. */
  recommended: PixelSize;
  /**
   * Aspect of the widest frame this field renders in, when that frame shows
   * less of the crop than the crop itself. The crop dialog outlines the band
   * it keeps. Null when every frame shows the whole crop.
   */
  wideScreenAspectRatio: number | null;
  /**
   * Uncropped fields only: the box the upload is scaled down to fit before it
   * is stored. Null for cropped fields, whose crop export is already `output`.
   */
  downscaleTo: ImageBounds | null;
}

/**
 * Wide-screen hero frames (bidder catalog hero from 1025px up, admin
 * Configure hero) are 4:1. Their CSS carries the same ratio. Change them
 * together. Below 1025px the bidder hero is 16:9, the crop's own ratio.
 */
export const WIDE_HERO_ASPECT = 4;

function megabytes(mb: number): number {
  return mb * BYTES_PER_MB;
}

function typesLabel(parts: readonly string[]): string {
  if (parts.length === 1) return parts[0]!;
  if (parts.length === 2) return `${parts[0]} or ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')} or ${parts[parts.length - 1]}`;
}

function buildSpec(input: {
  kind: ImageFieldKind;
  label: string;
  types: readonly string[];
  typeNames: readonly string[];
  maxMb: number;
  aspectRatio: number | null;
  cropLabel: string | null;
  output: PixelSize | null;
  warnBelow: PixelSize;
  recommended: PixelSize;
  wideScreenAspectRatio?: number | null;
  downscaleTo?: ImageBounds | null;
}): ImageFieldSpec {
  return {
    kind: input.kind,
    label: input.label,
    acceptedTypes: input.types,
    acceptAttr: input.types.join(','),
    typesLabel: typesLabel(input.typeNames),
    maxMb: input.maxMb,
    maxBytes: megabytes(input.maxMb),
    aspectRatio: input.aspectRatio,
    cropLabel: input.cropLabel,
    output: input.output,
    warnBelow: input.warnBelow,
    recommended: input.recommended,
    wideScreenAspectRatio: input.wideScreenAspectRatio ?? null,
    downscaleTo: input.downscaleTo ?? null,
  };
}

/**
 * Item / bundle photo — static frame on export (GIF/WebP flatten). 4:3 matches
 * the item page hero, gallery, catalog cards and bid sheet in bidder-web and
 * the admin catalog grid, so the crop frame is what bidders see there. Square
 * thumbnails trim the sides via object-fit: cover. Keep this in step with
 * those frames if either changes.
 */
export const PHOTO_SPEC: ImageFieldSpec = buildSpec({
  kind: ImageFieldKind.PHOTO,
  label: 'Photo',
  types: [ImageMime.PNG, ImageMime.JPEG, ImageMime.GIF, ImageMime.WEBP],
  typeNames: ['PNG', 'JPG', 'GIF', 'WebP'],
  maxMb: 5,
  aspectRatio: 4 / 3,
  cropLabel: '4:3',
  output: { width: 1200, height: 900 },
  warnBelow: { width: 800, height: 600 },
  recommended: { width: 1200, height: 900 },
});

/** Event cover hero — 16:9, raster only (no GIF to avoid an animated hero). */
export const COVER_SPEC: ImageFieldSpec = buildSpec({
  kind: ImageFieldKind.COVER,
  label: 'Cover image',
  types: [ImageMime.PNG, ImageMime.JPEG],
  typeNames: ['PNG', 'JPG'],
  maxMb: 10,
  aspectRatio: 16 / 9,
  cropLabel: '16:9',
  output: { width: 1600, height: 900 },
  warnBelow: { width: 1280, height: 720 },
  recommended: { width: 1600, height: 900 },
  wideScreenAspectRatio: WIDE_HERO_ASPECT,
});

/** Email / promo banner — 3:1, PNG/JPG only (email clients drop SVG). */
export const BANNER_SPEC: ImageFieldSpec = buildSpec({
  kind: ImageFieldKind.BANNER,
  label: 'Banner',
  types: [ImageMime.PNG, ImageMime.JPEG],
  typeNames: ['PNG', 'JPG'],
  maxMb: 5,
  aspectRatio: 3,
  cropLabel: '3:1',
  output: { width: 1200, height: 400 },
  warnBelow: { width: 900, height: 300 },
  recommended: { width: 1200, height: 400 },
});

/**
 * Logo — no crop; SVG never low-res-warns; raster warns below 240×80.
 *
 * Raster logos are stored at most 800×240. The largest frames are the 200×60pt
 * receipt PDF box (which embeds the stored bytes in every receipt attachment),
 * the 130px-wide email header and the 64px-tall registration header, so the
 * box covers each at 2× or better with room for wide logos.
 */
export const LOGO_SPEC: ImageFieldSpec = buildSpec({
  kind: ImageFieldKind.LOGO,
  label: 'Logo',
  types: [ImageMime.PNG, ImageMime.JPEG, ImageMime.SVG],
  typeNames: ['PNG', 'JPG', 'SVG'],
  maxMb: 2,
  aspectRatio: null,
  cropLabel: null,
  output: null,
  warnBelow: { width: 240, height: 80 },
  recommended: { width: 360, height: 128 },
  downscaleTo: { maxWidth: 800, maxHeight: 240 },
});

/** The accept / size / downscale contract for an image upload with no crop step. */
export type ImageUploadRules = Pick<
  ImageFieldSpec,
  'acceptedTypes' | 'acceptAttr' | 'typesLabel' | 'maxMb' | 'maxBytes'
> & { downscaleTo: ImageBounds };

/**
 * Image inserted into an email body from the media library. Stored at most
 * 1200px wide: 2× the 600px email body. Height is left free so a tall flyer
 * keeps its full width. GIF passes through unresized (see downscaleImageFile).
 */
export const EMAIL_IMAGE_UPLOAD: ImageUploadRules = {
  acceptedTypes: [ImageMime.PNG, ImageMime.JPEG, ImageMime.GIF, ImageMime.WEBP],
  acceptAttr: [ImageMime.PNG, ImageMime.JPEG, ImageMime.GIF, ImageMime.WEBP].join(','),
  typesLabel: typesLabel(['PNG', 'JPG', 'GIF', 'WebP']),
  maxMb: 5,
  maxBytes: megabytes(5),
  downscaleTo: { maxWidth: 1200, maxHeight: null },
};

export const IMAGE_SPECS = {
  PHOTO: PHOTO_SPEC,
  COVER: COVER_SPEC,
  BANNER: BANNER_SPEC,
  LOGO: LOGO_SPEC,
} as const;

export function isSvgMime(mimeType: string): boolean {
  return mimeType === ImageMime.SVG;
}

/** True when the field opens a crop step for this file (Logo + SVG skip). */
export function requiresCrop(spec: ImageFieldSpec, file: File): boolean {
  if (spec.aspectRatio === null || spec.output === null) return false;
  if (isSvgMime(file.type)) return false;
  return true;
}

export function typeError(typesLabel: string): string {
  return `That file type isn't supported. Use ${typesLabel}.`;
}

export function sizeError(maxMb: number): string {
  return `That image is too large. Keep it under ${maxMb}MB.`;
}

/**
 * Format + byte-size gate. Run BEFORE opening any crop UI so a doomed file
 * never costs a crop interaction. Returns an error string, or null when ok.
 */
export function validateImageFile(
  file: File,
  spec: Pick<ImageFieldSpec, 'acceptedTypes' | 'typesLabel' | 'maxMb' | 'maxBytes'>,
): string | null {
  if (!spec.acceptedTypes.includes(file.type)) return typeError(spec.typesLabel);
  if (file.size > spec.maxBytes) return sizeError(spec.maxMb);
  return null;
}

/** "1200 × 1200 px" — one spelling for every dimension pair. */
export function formatPixelSize(size: PixelSize): string {
  return `${size.width} × ${size.height} px`;
}

/**
 * Soft low-res check against the field's warn threshold. SVG never warns —
 * vectors scale. Returns true when a raster source is under either edge.
 */
export function isLowResolution(
  natural: PixelSize,
  spec: ImageFieldSpec,
  options: { mimeType?: string } = {},
): boolean {
  if (options.mimeType && isSvgMime(options.mimeType)) return false;
  return natural.width < spec.warnBelow.width || natural.height < spec.warnBelow.height;
}

/**
 * "Low resolution: 480 × 360 px. It may look blurry at display size. Use at
 * least 1200 × 900 px." Warn-only: names the size, the cost, and the target.
 */
export function lowResNote(
  natural: PixelSize,
  consequence: string,
  recommended?: PixelSize,
): string {
  const advice = recommended
    ? `${consequence} Use at least ${formatPixelSize(recommended)}.`
    : consequence;
  return `Low resolution: ${formatPixelSize(natural)}. ${advice}`;
}

const DIMENSION_READ_ERROR = 'That image could not be read.';

/**
 * Natural pixel size of a raster (or bitmap-decodable) image. Prefers
 * `createImageBitmap`; falls back to `HTMLImageElement` naturalWidth/Height.
 * Callers should skip this for SVG — vectors have no fixed pixel size and
 * never enter the low-res path.
 */
export async function readImageDimensions(source: Blob): Promise<PixelSize> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(source);
      const size = { width: bitmap.width, height: bitmap.height };
      bitmap.close();
      if (size.width > 0 && size.height > 0) return size;
    } catch {
      // Fall through to the Image path — some Safari builds reject exotic codecs.
    }
  }

  const objectUrl = URL.createObjectURL(source);
  try {
    const image = new Image();
    // Handlers before src — a cached image can fire load synchronously.
    const viaLoad = new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error(DIMENSION_READ_ERROR));
    });
    image.src = objectUrl;
    if (!(image.complete && image.naturalWidth > 0)) {
      try {
        if (typeof image.decode === 'function') await image.decode();
        else await viaLoad;
      } catch {
        await viaLoad;
      }
    }
    if (image.naturalWidth <= 0 || image.naturalHeight <= 0) {
      throw new Error(DIMENSION_READ_ERROR);
    }
    return { width: image.naturalWidth, height: image.naturalHeight };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}
